import {
	BlogDetailsPage,
	ChatBot,
	NextPageHead,
	PageView,
	VWOExperimentPush,
} from '@aspendental/shared-components-web';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import DetermineIndexing from '../utils/determine-indexing';
import { getDeviceTypeFromHeaders } from '../utils/get-device-type-from-headers';
import getHostFromHeaders from '../utils/get-host-from-headers';
import { getUserCoordinates } from '../utils/get-user-coordinates';

import { BlogDetailsPageByPathDocument, BlogDetailsPageFragment, Page as IPage } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import { fetchAppData } from '@src/pages/utils/app-data-fetcher';
import choosePreview from '@src/pages/utils/choose-preview';
import convertPathToBreadCrumbTitle from '@src/pages/utils/convert-path-to-breadcrumb-title';
import getConfigByBrand from '@src/pages/utils/get-config-by-brand';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import getThemeByDomainName from '@src/pages/utils/get-theme-by-domain-name';
import resolveTagTheme from '@src/pages/utils/resolve-tag-theme';

const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
	const { siteURL, features, featureFlags, logo } = props.config as BrandThemeConfig;
	const { url: logoUrl } = props.appData?.logo || {};
	const { pageData, path, breadcrumbsData, preview, domain } = props;
	const { metaTitle, metaDescription, canonical, indexed, analyticsContext, ogImage } = pageData;

	return (
		<>
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} />
			<PageView analyticsContext={analyticsContext} siteSection="Marketing" />
			<NextPageHead
				path={path}
				title={metaTitle}
				indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)}
				description={metaDescription}
				canonical={canonical}
				originUrl={siteURL}
				breadcrumbTitle={breadcrumbsData.breadcrumbTitle}
				breadcrumbs={breadcrumbsData.breadcrumbsCollection.items}
				ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark}
			/>
			<BlogDetailsPage {...pageData} />
			{features.chat.enabled && (
				<ChatBot
					url={features.chat.chatBotURL}
					isIconButton={features.chat.isIconButton}
					logoUrl={logoUrl}
					featureList={featureFlags.featureList}
				/>
			)}
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ params, query, locale, req, res, preview = false }) => {
	if (!params?.slug) {
		return {
			notFound: true,
		};
	}
	const domain = getHostFromHeaders(req?.headers);
	const deviceType = getDeviceTypeFromHeaders(req.headers);
	const theme = getThemeByDomainName(domain);
	const tagTheme = resolveTagTheme(req.headers, theme);
	const logger = getLogger('blog-detail-page', tagTheme);
	try {
		const brandConfig = getConfigByBrand(tagTheme);
		preview = choosePreview(domain, preview);
		const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
		const gqlClient = getClient(preview);
		if (gqlClient === null) {
			logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
			throw new Error('gqlClient is null');
		}

		const appData = await fetchAppData(gqlClient, appName, preview, logger);

		if (!appData) {
			return {
				notFound: true,
			};
		}

		const slugs = params.slug as string[];

		// this path value needed for page indexing
		const path = '/' + slugs.join('/');
		logger.info(path);

		//only the last section in the url path is the slug to query
		const pathForQuery = slugs[slugs.length - 1];
		const title = brandConfig.name;
		const pageData = await gqlClient
			.query(BlogDetailsPageByPathDocument, { path: pathForQuery, title, preview })
			.toPromise();
		if (pageData.error) {
			logger.error(`CONTENTFUL_ERROR_BLOG_DETAILS_PAGE: ${pageData.error}`);
		}
		const page = pageData.data.blogDetailsPageCollection?.items[0] as BlogDetailsPageFragment;

		if (!page) {
			logger.error(`COULD_NOT_FIND_THE_BLOG_SLUG: ${pathForQuery}`);
			return {
				notFound: true,
			};
		}

		// breadcrumb data generation for blog detail page
		const {
			features: {
				blogs: { root },
			},
		} = brandConfig;
		const breadcrumbsData = {
			// current page title from the slug value
			breadcrumbTitle: convertPathToBreadCrumbTitle(slugs[slugs.length - 1]),
			// current page path
			path,
			// just blog list page in the hierarchy now
			// need to add category pages once that is built
			breadcrumbsCollection: {
				items: [
					{
						breadcrumbTitle: 'Home',
						path: '/',
					} as IPage,
					{
						breadcrumbTitle: convertPathToBreadCrumbTitle(root),
						path: root,
					} as IPage,
				],
			},
		};

		const { hasIpBasedCoordinates, coordinates } = getUserCoordinates(req, logger);
		if (hasIpBasedCoordinates) {
			logger.info(`CDN_GEO_LOCATION_FOUND: [${coordinates.latitude}, ${coordinates.longitude}]`);
		} else {
			logger.info(`CDN_GEO_LOCATION_MISS`);
		}

		const transformedServices = getConfigServices(brandConfig);

		// Resolve standalone VWO FME flags (layout-level header swap, etc.)
		const { flags: serverFeatureFlags, decisions: vwoDecisions } = await resolveFeatureFlags(
			LAYOUT_FEATURE_FLAGS,
			brandConfig,
			req,
			res,
			query,
			logger
		);

		return {
			props: {
				...(await getServerSideTranslations(locale)),
				env: {
					ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
					CC_DENTRINO_CLIENT_ID: process.env.CC_DENTRINO_CLIENT_ID ?? '',
					CC_DENTRINO_CLIENT_SECRET: process.env.CC_DENTRINO_CLIENT_SECRET ?? '',
					DENTRINO_CLIENT_ID: process.env.DENTRINO_CLIENT_ID,
					DENTRINO_CLIENT_SECRET: process.env.DENTRINO_CLIENT_SECRET,
				},
				config: {
					...brandConfig,
					...(appData.featureFlags ? { featureFlags: appData.featureFlags } : {}),
					services: transformedServices,
					schemaUrl: `${brandConfig.siteURL}${req.url}`,
				},
				pageData: page,
				user: {
					coordinates,
					hasIpBasedCoordinates,
				},
				appData,
				footerData: appData?.footer,
				path,
				breadcrumbsData,
				preview,
				deviceType,
				domain: domain ?? '',
				serverFeatureFlags,
				vwoDecisions,
			},
		};
	} catch (e) {
		logger.error(`BLOG_DETAIL_PAGE_LOAD_FAILURE: ${e}`);
		return {
			notFound: true,
		};
	}
};

export default Page;
